import { useLanguage } from '@/lib/i18n';
import { motion } from 'framer-motion';
import { Clock3, Handshake, Scale, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';

const EmployersSection = () => {
  const { tr } = useLanguage();

  const pathways = [
    {
      id: 'employers-anu',
      icon: Clock3,
      title: tr('employers.anu.title'),
      desc: tr('employers.anu.desc'),
      bullets: [
        tr('employers.anu.benefit1'),
        tr('employers.anu.benefit2'),
        tr('employers.anu.benefit3'),
      ],
      cta: tr('employers.anu.cta'),
    },
    {
      id: 'employers-placement',
      icon: Handshake,
      title: tr('employers.placement.title'),
      desc: tr('employers.placement.desc'),
      bullets: [
        tr('employers.placement.benefit1'),
        tr('employers.placement.benefit2'),
        tr('employers.placement.benefit3'),
      ],
      cta: tr('employers.placement.cta'),
    },
  ];

  const trustItems = [
    { icon: Scale, title: tr('employers.legal.title'), desc: tr('employers.legal.desc') },
    { icon: Users, title: tr('employers.integration.title'), desc: tr('employers.integration.desc') },
  ];

  return (
    <section id="employers" className="scroll-mt-24 py-16 md:py-24 bg-background">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-3xl mx-auto mb-14"
        >
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground">{tr('employers.title')}</h2>
          <p className="mt-4 text-muted-foreground text-lg">{tr('employers.subtitle')}</p>
          <p className="mt-6 text-sm text-muted-foreground border-l-4 border-primary pl-3">
            {tr('about.austrojob')}
          </p>
        </motion.div>
        <div className="grid md:grid-cols-2 gap-8">
          {pathways.map((item, i) => (
            <motion.div
              key={item.id}
              id={item.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="bg-card rounded-xl p-8 card-shadow border border-border"
            >
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                <item.icon className="w-6 h-6 text-primary" />
              </div>
              <h4 className="font-heading text-lg font-semibold text-foreground mb-2">{item.title}</h4>
              <p className="text-muted-foreground text-sm">{item.desc}</p>
              <ul className="mt-5 space-y-2">
                {item.bullets.map((bullet, bulletIndex) => (
                  <li key={bulletIndex} className="flex items-start gap-2 text-sm text-foreground">
                    <span className="mt-1 h-1.5 w-1.5 rounded-full bg-primary/80" aria-hidden="true" />
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-6">
                <Button asChild>
                  <a href="/#contact-form">{item.cta}</a>
                </Button>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-10 grid md:grid-cols-2 gap-6">
          {trustItems.map((item, i) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.15 + i * 0.1 }}
              className="bg-card rounded-xl p-6 card-shadow"
            >
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-3">
                <item.icon className="w-5 h-5 text-primary" />
              </div>
              <h4 className="font-heading text-base font-semibold text-foreground mb-2">{item.title}</h4>
              <p className="text-muted-foreground text-sm">{item.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default EmployersSection;
